import './MachineHistoryChart.css'

import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CurrentSession } from '@/services/repository/session'
import { Services } from '@/services/services'
import { useNavigate } from 'react-router-dom'
import { StaticPaginatedTable, StaticPaginatedTableState } from '@/components/search/StaticPaginatedTable'

import { User } from '@/services/repository/users'
import { Translates } from '@/services/repository/i18n'
import { SessionsResponse, SessionStat } from '@/pages/communal/statistics/components/common/data'
import { formatDate, getTodayRange, secondsToDHM } from '@/common/time'
import { TimeDHM } from '@/components/Elements/time'
import { UserDeviceHistoriesData, UserDeviceHistoriesMachine } from '@/pages/communal/statistics/requests/histories'
import { CommunalUserPathMap } from '@/pages/communal/statistics/userstatistic/components/Map/PathMap'

import {
  Chart as ChartJS,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  ChartOptions,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { Line } from "react-chartjs-2";
import { HitMapNav, HitMapNavData, HitMapNavURLParams, hitsMap } from '@/pages/communal/HitMap'
import { HitMapComponent, MapRow } from '@/components/Map/HitMap'
import { HitText } from '@/components/hit/HitText'
import { Machine } from '@/services/model/machine'
import { useAppContext } from '@/context/app'

ChartJS.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend
);

export interface MachineBucket {
  active: boolean
  bucket: string // ISO 8601 date string
  onFire: boolean
  maxHitX: number
  maxHitY: number
  maxHitZ: number
  maxSpeed: number
  maxCurrent: number
  maxEngineHours: number
  minUbat: number

  countHits: number
  minSOC: number
  avgHitX: number
  avgHitY: number
  avgHitZ: number
}

interface Group {
  name: string
}

const allGroup = {
  name: 'all'
}

const groups: Group[] = [allGroup, {
  name: 'hits'
}, {
    name: 'active'
  }, {
    name: 'move'
  }, {
    name: 'battery'
  }]

function isActiveGroup(names: string[], group: Group | undefined) {
  return !(group === undefined || group.name === 'all' || names.indexOf(group.name) !== -1)
}

export const MachineHistoryChart = ({ buckets }: {
  buckets: MachineBucket[]
}) => {
  const navigate = useNavigate()
  const { translates } = useAppContext()
  const [ group, setGroup ] = useState<Group>(allGroup)

  if (!translates) {
    return <div className='spinner'></div>
  }

  const calcChartData = (group: Group | undefined) => {
    let labels = buckets.map(d => d.bucket)
    return {
      labels,
      datasets: [
        {
          label: translates.for('page.statistics.machinehistorychart.chart_hit_count'),
          data: buckets.map(d => ({ x: d.bucket, y: d.countHits })),
          borderColor: "rgba(54, 162, 235, 0.8)",
          backgroundColor: "rgba(54, 162, 235, 0.1)",
          tension: 0.4,
          yAxisID: "main",
          hidden: isActiveGroup(['hits'], group),
        },
        {
          label: translates.for('page.statistics.machinehistorychart.chart_max_hit_x'),
          data: buckets.map(d => ({ x: d.bucket, y: d.maxHitX })),
          borderColor: "rgba(255, 206, 86, 0.8)",
          backgroundColor: "rgba(255, 206, 86, 0.1)",
          tension: 0.4,
          yAxisID: "main",
          hidden: isActiveGroup(['hits'], group),
        },
        {
          label: translates.for('page.statistics.machinehistorychart.chart_max_hit_y'),
          data: buckets.map(d => ({ x: d.bucket, y: d.maxHitY })),
          borderColor: "rgba(75, 192, 192, 0.8)",
          backgroundColor: "rgba(75, 192, 192, 0.1)",
          tension: 0.4,
          yAxisID: "main",
          hidden: isActiveGroup(['hits'], group)
        },
        {
          label: translates.for('page.statistics.machinehistorychart.chart_max_speed'),
          data: buckets.map(d => ({ x: d.bucket, y: d.maxSpeed })),
          borderColor: "rgba(255, 99, 71, 0.8)",
          backgroundColor: "rgba(255, 99, 71, 0.1)",
          tension: 0.4,
          yAxisID: "main",
          hidden: isActiveGroup(['hits', 'move'], group)
        },
        {
          label: translates.for('page.statistics.machinehistorychart.chart_avr_hit_x'),
          data: buckets.map(d => ({ x: d.bucket, y: d.avgHitX })),
          borderColor: "rgba(253, 219, 133, 0.8)",
          backgroundColor: "rgba(255, 206, 86, 0.1)",
          tension: 0.4,
          yAxisID: "main",
          hidden: isActiveGroup(['hits'], group)
        },
        {
          label: translates.for('page.statistics.machinehistorychart.chart_avr_hit_y'),
          data: buckets.map(d => ({ x: d.bucket, y: d.avgHitY })),
          borderColor: "rgba(126, 189, 189, 0.8)",
          backgroundColor: "rgba(75, 192, 192, 0.1)",
          tension: 0.4,
          yAxisID: "main",
          hidden: isActiveGroup(['hits'], group)
        },
        // {
        //   label: translates.for('page.statistics.machinehistorychart.chart_avr_speed'),
        //   data: buckets.map(d => ({ x: d.bucket, y: d.maxSpeed })),
        //   borderColor: "rgba(255, 183, 171, 0.8)",
        //   backgroundColor: "rgba(255, 99, 71, 0.1)",
        //   tension: 0.4,
        //   yAxisID: "main",
        // },
        {
          label: translates.for('page.statistics.machinehistorychart.chart_min_soc'),
          data: buckets.map(d => ({ x: d.bucket, y: d.minSOC })),
          borderColor: "rgba(57, 40, 255, 0.8)",
          backgroundColor: "rgba(255, 99, 71, 0.1)",
          tension: 0.4,
          yAxisID: "main",
          hidden: isActiveGroup(['battery'], group)
        },
        {
          label: translates.for('page.statistics.machinehistorychart.chart_min_ubat'),
          data: buckets.map(d => ({ x: d.bucket, y: d.minUbat })),
          borderColor: "rgba(251, 40, 255, 0.8)",
          backgroundColor: "rgba(222, 64, 236, 0.5)",
          tension: 0.4,
          yAxisID: "main",
          hidden: isActiveGroup(['battery'], group)
        },
        {
          label: translates.for('page.statistics.machinehistorychart.chart_max_current'),
          data: buckets.map(d => ({ x: d.bucket, y: d.maxCurrent })),
          borderColor: "rgba(255, 234, 40, 0.8)",
          backgroundColor: "rgba(165, 135, 0, 0.5)",
          tension: 0.4,
          yAxisID: "main",
          hidden: isActiveGroup(['battery'], group)
        },
        {
          label: translates.for('page.statistics.machinehistorychart.chart_max_mth'),
          data: buckets.map(d => ({ x: d.bucket, y: d.maxEngineHours })),
          borderColor: "rgba(0, 28, 155, 0.8)",
          backgroundColor: "rgba(0, 28, 155, 0.5)",
          tension: 0.4,
          yAxisID: "main",
          hidden: true//isActiveGroup(['move'], group)
        },

        // {
        //   label: translates.for('page.statistics.machinehistorychart.chart_max_hit_z'),
        //   data: buckets.map(d => ({ x: d.bucket, y: d.maxHitZ })),
        //   borderColor: "rgba(153, 102, 255, 0.8)",
        //   backgroundColor: "rgba(153, 102, 255, 0.1)",
        //   tension: 0.4,
        //   yAxisID: "main",
        // },
        {
          label: translates.for('page.statistics.machinehistorychart.chart_fire'),
          data: buckets.map(d => ({ x: d.bucket, y: d.onFire ? 1 : 0 })),
          borderColor: "rgba(255, 99, 132, 0.8)",
          backgroundColor: "rgba(255, 99, 132, 0.1)",
          borderDash: [8, 4],
          tension: 0,
          yAxisID: "flags",
          hidden: isActiveGroup(['active'], group)
        },
        {
          label: translates.for('page.statistics.machinehistorychart.chart_active'),
          data: buckets.map(d => ({ x: d.bucket, y: d.active ? 1 : 0 })),
          borderColor: "rgba(166, 127, 9, 0.78)",
          backgroundColor: "rgba(252, 255, 99, 0.1)",
          borderDash: [8, 4],
          tension: 0,
          yAxisID: "flags",
          hidden: isActiveGroup(['active'], group)
        }
      ],
    }
  };

  const options: ChartOptions<"line"> = {
    responsive: true,
    plugins: {
      legend: { display: true },
      tooltip: {
        mode: "index",
        intersect: false
      },
      title: {
        display: true,
        text: translates.for('page.statistics.machinehistorychart.title'),
      },
    },
    scales: {
      x: {
        type: "time",
        time: {
          unit: "minute",
          tooltipFormat: "yyyy-MM-dd HH:mm",
          displayFormats: { minute: "yyyy-MM-dd HH:mm" }
        },
        title: { display: true, text: "Frame Time" }
      },
      main: {
        type: "linear",
        position: "left",
        title: { display: true, text: translates.for('page.statistics.machinehistorychart.chart_values_label') }
      },
      flags: {
        type: "linear",
        position: "right",
        min: -0.1,
        max: 1.1,
        grid: { drawOnChartArea: false },
        ticks: {
          callback: (v) => v === 1 ? translates.for('bool.true') : v === 0 ? translates.for('bool.false') : "",  // Show bool meaning
        },
        title: { display: true, text: translates.for('page.statistics.machinehistorychart.chart_flags_label') }
      },
    },
  };

  const chartData = useMemo(() => {
    return calcChartData(group)
  }, [group])

  return <div className='communal-statistics-machine-history-chart'>
    <div style={{ width: '100%' }}>
      <div className='group-selector'>
        {groups.map((group: Group) => <span onClick={() => setGroup(group)}>
          {translates.for(`page.statistics.machinehistorychart.groups.${group.name}`)}
        </span>)}
      </div>
      <Line data={chartData} options={options} />
    </div>
  </div>
}

